(function () {
  'use strict';

  if (customElements.get('wim-results-before-after')) return;

  const RESIZE_DEBOUNCE_MS = 100;

  class WimResultsBeforeAfter extends HTMLElement {
    connectedCallback() {
      // Flickity locks `.flickity-viewport` inline height at init with
      // `adaptiveHeight:false` and `resize()` won't reset it. compare-images.js
      // caches `data-image-element`/`data-image-holder` widths in pixels and
      // only refreshes on `theme:resize`. We clear both caches manually before
      // asking Flickity to re-measure against the CSS-driven cell height.
      const triggerResize = () => {
        const slider = this.querySelector('before-after-slider');
        if (!slider) return;
        const viewport = slider.querySelector('.flickity-viewport');
        if (viewport) viewport.style.height = '';
        this.querySelectorAll('[data-image-element], [data-image-holder]').forEach((el) => {
          el.style.width = '';
        });
        slider.flkty?.resize();
        this.querySelectorAll('compare-images').forEach((el) => {
          el.setOverlapImageSize?.();
        });
      };
      this._ro = new ResizeObserver((entries) => {
        // Skip when hidden (tab inactive, display:none ancestor) — compare-images
        // would cache width:0 and lock the comparator until the next event.
        if (!entries[0] || entries[0].contentRect.width <= 0) return;
        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(triggerResize, RESIZE_DEBOUNCE_MS);
      });
      this._ro.observe(this);

      const total = Number(this.dataset.resultsCount) || 0;
      if (total <= 1) return;

      const tagLabel = this.dataset.tagLabel || '';
      const separator = this.dataset.tagSeparator || '';
      const slides = this.querySelectorAll('[data-content-slide]');
      const pills = this.querySelectorAll('[data-pill-index]');
      const nextButtons = this.querySelectorAll('[data-next-result]');

      let currentIndex = 0;

      this._ac = new AbortController();
      const { signal } = this._ac;

      const pad = (n) => String(n).padStart(2, '0');

      const updateContent = (index) => {
        currentIndex = index;
        slides.forEach((slide) => {
          const isTarget = Number(slide.dataset.index) === index;
          slide.classList.toggle('is-active', isTarget);
          if (isTarget) {
            const counter = slide.querySelector('[data-result-counter]');
            if (counter) {
              counter.textContent = `${tagLabel} ${pad(index + 1)} ${separator} ${pad(total)}`;
            }
          }
        });
      };

      nextButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const nextIndex = (currentIndex + 1) % total;
          // Delegate to pill so the base slider handles wrapAround and aria-selected;
          // the MutationObserver below will sync the content track.
          if (pills.length > 0) {
            pills[nextIndex]?.click();
          } else {
            updateContent(nextIndex);
          }
        }, { signal });
      });

      if (pills.length > 0) {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.attributeName === 'aria-selected' &&
              mutation.target.getAttribute('aria-selected') === 'true'
            ) {
              const pillIndex = Number(mutation.target.dataset.pillIndex);
              if (pillIndex !== currentIndex) {
                updateContent(pillIndex);
              }
            }
          }
        });

        pills.forEach((pill) => {
          observer.observe(pill, { attributes: true, attributeFilter: ['aria-selected'] });
        });

        this._observer = observer;
      }
    }

    disconnectedCallback() {
      this._ac?.abort();
      this._observer?.disconnect();
      this._observer = null;
      this._ro?.disconnect();
      this._ro = null;
      if (this._resizeTimer) {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = null;
      }
    }
  }

  customElements.define('wim-results-before-after', WimResultsBeforeAfter);
})();
